import { ReactNode, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLocation } from 'wouter';
import { useAuthz } from '@/hooks/useAuthz';
import { DEV_AUTH_BYPASS } from '@/config/environment';
import { hasAuthToken } from '@/lib/auth-utils';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Require admin role for this route
   */
  requireAdmin?: boolean;
  /**
   * Require instructor role or higher for this route
   */
  requireInstructor?: boolean;
  /**
   * BRITE: require manager tier (stored 'instructor') or admin.
   * Intake review, PIN issuance, events, Generator, user list.
   */
  requireManager?: boolean;
  /**
   * Require premium membership for this route (library, avatars, chamber, etc.)
   * Use this for platform features that require paid membership
   */
  requirePremium?: boolean;
  /**
   * @deprecated Use requirePremium instead
   * Kept for backwards compatibility - maps to requirePremium
   */
  requireSubscription?: boolean;
}

export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireInstructor = false,
  requireManager = false,
  requirePremium = false,
  requireSubscription = false, // DEPRECATED - maps to requirePremium
}: ProtectedRouteProps) {
  // Map deprecated prop to new prop
  const needsPremium = requirePremium || requireSubscription;
  // BRITE manager tier === instructor tier (label-mapped); reuse the same check
  const needsInstructor = requireInstructor || requireManager;

  // Call all hooks FIRST (before any conditional returns)
  const { user, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { isAdmin, isInstructor: checkInstructor, isPremium: checkPremium } = useAuthz();

  // Prevent search engines from indexing member-only pages
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prev = meta?.getAttribute('content') ?? null;
    if (meta) {
      meta.setAttribute('content', 'noindex, nofollow');
    } else {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex, nofollow');
      document.head.appendChild(meta);
    }
    return () => {
      const m = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (m) {
        if (prev !== null) {
          m.setAttribute('content', prev);
        } else {
          m.remove();
        }
      }
    };
  }, []);

  // ✅ Call useEffect BEFORE any returns (Rules of Hooks)
  useEffect(() => {
    // BULLETPROOF Development auth bypass - skip auth checks in development
    if (DEV_AUTH_BYPASS) {
      return;
    }

    // Don't run effects during initial loading
    if (isLoading) {
      return;
    }

    // CRITICAL FIX: Check for token first to prevent race condition
    // If there's a valid token but user isn't loaded yet, wait for it
    const tokenExists = hasAuthToken();

    if (tokenExists && !isAuthenticated) {
      // Token exists but user state not loaded yet - wait for auth to complete
      // This prevents redirect during the brief moment after login when token is set
      // but user state hasn't propagated yet
      return;
    }

    // Handle unauthenticated users (no token AND no user)
    if (!isAuthenticated) {
      setLocation('/sign-in');
      return;
    }

    // Handle role-based authorization (SIMPLIFIED)
    if (requireAdmin && !isAdmin()) {
      setLocation('/dashboard'); // /instructor route removed in BRITE
      return;
    }

    if (needsInstructor && !checkInstructor()) {
      setLocation('/dashboard');
      return;
    }

    if (needsPremium && !checkPremium()) {
      setLocation('/dashboard');
      return;
    }
  }, [
    isLoading,
    isAuthenticated,
    user?.role,
    requireAdmin,
    needsInstructor,
    needsPremium,
    isAdmin,
    checkInstructor,
    checkPremium,
    setLocation,
  ]);

  // Development auth bypass for render
  if (DEV_AUTH_BYPASS) {
    return <>{children}</>;
  }

  // Show loader during initial auth loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // CRITICAL FIX: If token exists but user not loaded yet, show loader instead of redirecting
  // This handles the race condition after login when token is set but state hasn't propagated
  const tokenExists = hasAuthToken();
  if (tokenExists && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Don't render children if not authenticated - useEffect will redirect
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Don't render children if unauthorized - useEffect will redirect
  // Show brief spinner while redirect is happening
  if (
    (requireAdmin && !isAdmin()) ||
    (needsInstructor && !checkInstructor()) ||
    (needsPremium && !checkPremium())
  ) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return <>{children}</>;
}
